import React, { useState } from 'react';
import { Container, Row, Col, Form, Accordion, Button } from 'react-bootstrap';
import login from '../assets/images/login-help.png';
interface FAQItem {
    question: string;
    answer: React.ReactNode; // Change the type to React.ReactNode
  }
const faqs :FAQItem[] = [
    {
      question: "I want to set up a myindici account. What do I do?",
      answer: (
        <>
          <p className='answer_1 mb-3'>
            First, you need to contact your medical practice and inform them that you wish to have a myindici account. 
            Medical practices across New Zealand use several different practice management systems, 
            and only those using indici will be able to offer you a myindici account.
          </p>
          <p className='answer_1 mb-3'>
            If your practice is using indici, once you have confirmed your wish to create an account, they will 
            register you with myindici.
          </p>
          <p className='answer_1'>
            You will receive two emails, one containing a link to the log in page, 
            one containing a temporary password. Click on the link.
            When you come to the log in screen, you should enter your email address in the Username field and your 
            temporary password in the Password field, then click log in. You will be prompted to update your password; 
            when doing so, just be aware that your password will need to be suitably complex with a mixture of
             letters, numbers and special characters (%&* etc.)          
            Once you have updated the password, you will be able to log in from now on with that new password.
          </p>
        </>
      ),
    },
    {
      question: "What browser can I access myindici from?",
      answer: (
        <>
          <p className='answer_1'>
            myindici has been designed for Google Chrome and will work best when used through the Google Chrome browser. 
            If you are experiencing any access or display issues when logging in online, it is a good idea to check and ensure you are using Google Chrome.
          </p>
        </>
      ),
    },
    {
      question: "Can I access myindici outside New Zealand and Australia?",
      answer: (
        <>
          <p className='answer_1'>
            No. As an important security measure designed to ensure your data is kept safe, 
            access to myindici is restricted to New Zealand and Australia.
          </p>
        </>
      ),
    },
    {
      question: "What do I do if I have forgotten my password?",
      answer: (
        <>
          <p className='answer_1'>
            If you have forgotten your password, that’s no problem. On the log in screen click Reset Now as shown below. 
            In the window that opens, enter your email and click Send Password.
          </p>
          <div className="d-flex justify-content-center">
            <img src={login} alt='...' width={500} />
          </div>
          <p className='answer_1'>
            You will then receive a temporary password via email, allowing you to log in.
          </p>
        </>
      ),
    },
    {
      question: "When I click on the myindici app, all I see is a white screen?",
      answer: (
        <>
          <p className='answer_1'>
            The most likely explanation for this is that you haven’t updated to myindici 2.0. 
            You can update by uninstalling myindici 1.0 and then installing myindici 2.0 which you will find on the app store 
            (either Google Play Store or Apple App Store depending on your device). 
            All of your account information, including username and password, will remain the same on the new app. 
            Once you switch to myindici 2.0, you should be able to log in without any issues.
          </p>
        </>
      ),
    },
    {
      question: "How can I update my address on myindici?",
      answer: (
        <>
          <p className='answer_1'>
            In order to update your address, please contact your medical practice directly, 
            as they will have to update it within their records. 
            Once they do that, the updated address will be displayed on myindici.
          </p>
        </>
      ),
    },
    {
      question: "If I transfer medical practice, what happens to my myindici account?",
      answer: (
        <>
          <p className='answer_1 mb-3'>
            When you transfer to a new practice, you need to re-register for myindici with that new practice 
            in order to access your account. 
            This is because myindici accounts are linked to your registered practice,
            so that you can send messages to your provider, receive test results, etc.
          </p>
          <p className='answer_1'>
            In this scenario you should contact your new practice directly and ask them to register you for myindici. 
            Once they do and you have completed the registration process, you will be able to access your account again.
          </p>
        </>
      ),
    },
    {
      question: "What do I do if a section within the portal is greyed out or I can’t open it?",
      answer: (
        <>
          <p className='answer_1'>
            If a section (e.g. Messaging) is greyed out it means your practice hasn’t activated it for your portal account. 
            This could be due to any number of reasons, so the best thing to do is to contact your medical practice directly, 
            inform them which sections you do not have access to, and ask them to update your portal account settings in order to give you access.
          </p>
        </>
      ),
    },
  ];
function flattenReactNode(node: React.ReactNode): string {
  if (typeof node === 'string') {
    return node;
  } else if (React.isValidElement(node) && node.props.children) {
    return React.Children.toArray(node.props.children)
      .map(child => flattenReactNode(child))
      .join('');
  }
  return '';
}
function FAQs() {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const filteredFaqs = faqs.filter(faq => {
    const flatContent = faq.question.toLowerCase() + flattenReactNode(faq.answer).toLowerCase();
    return flatContent.includes(searchQuery.toLowerCase());
  });
  const handleExpandAll = () => {
    const allEventKeys = filteredFaqs.map((_, index) => index.toString());
    setExpandedItems(allEventKeys);
  };

  const handleCollapseAll = () => {
    setExpandedItems([]);
  };

  return (
    <Container className='py-5'>
      <Row>
        <Col md={2}></Col>
        <Col md={9}>
          <Row>
            <Col md={8}>
              <h1 className='mb-4 myindici-heading'>FAQs</h1>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Control
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Col>
        <Col md={1}></Col>
      </Row>
      <Row>
        <Col md={2}></Col>
        <Col md={9}>
          <Row className="mb-3">
            <Col md={6}></Col>
            <Col md={2}></Col>
            <Col md={2} className="">
              <Button onClick={handleExpandAll} className='w-100 expandAll'>
                Expand All
              </Button>
            </Col>
            <Col md={2} className="d-flex justify-content-around">
              <Button onClick={handleCollapseAll} className='w-100 collapsAll'>
                Collapse All
              </Button>
            </Col>
          </Row>
          <Accordion activeKey={expandedItems} onSelect={(eventKey: any | null) => {
            if (eventKey !== null) {
              setExpandedItems(prevState => {
                if (prevState.includes(eventKey)) {
                  return prevState.filter(item => item !== eventKey);
                } else {
                  return [...prevState, eventKey];
                }
              });
            }
          }}>
            {filteredFaqs.map((item, index) => (
              <Accordion.Item key={index} eventKey={index.toString()}>
                <Accordion.Header>{item.question}</Accordion.Header>
                <Accordion.Body className='answer_1'>{item.answer}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Col>
        <Col md={3}></Col>
      </Row>
    </Container>
  );
}

export default FAQs;
